import { useCallback, useEffect, useState } from "react";
import { ApiError, apiFetch } from "../lib/api";
import { config } from "../lib/config";
import { LOCAL_SEED, type RecordRow } from "../data/localSeed";
import { useAuth } from "../state/AuthContext";

type ListState = {
  rows: RecordRow[];
  loading: boolean;
  waking: boolean;
  error: string | null;
  source: "api" | "local";
  refresh: () => void;
  mutateStatus: (id: string, status: string) => Promise<void>;
};

export function useEntityList(entity: string): ListState {
  const { roleIds } = useAuth();
  const roleHeader = roleIds.join("|");
  const [rows, setRows] = useState<RecordRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [waking, setWaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "local">("local");
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      setWaking(false);

      if (!config.apiUrl) {
        if (!cancelled) {
          setRows(LOCAL_SEED[entity] ?? []);
          setSource("local");
          setLoading(false);
        }
        return;
      }

      try {
        const data = await apiFetch<{ rows: RecordRow[] }>(`/entities/${entity}`, {
          roleId: roleHeader,
        });
        if (!cancelled) {
          setRows(data.rows);
          setSource("api");
        }
      } catch (err) {
        if (err instanceof ApiError && err.waking) {
          if (!cancelled) setWaking(true);
        }
        if (!cancelled) {
          setRows(LOCAL_SEED[entity] ?? []);
          setSource("local");
          setError(err instanceof Error ? err.message : "Failed to load");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [entity, roleHeader, tick]);

  const mutateStatus = useCallback(
    async (id: string, status: string) => {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      if (!config.apiUrl) return;
      try {
        await apiFetch(`/entities/${entity}/${id}`, {
          method: "PATCH",
          roleId: roleHeader,
          body: { status },
        });
      } catch {
        refresh();
      }
    },
    [entity, roleHeader, refresh],
  );

  return { rows, loading, waking, error, source, refresh, mutateStatus };
}

export function useTasks() {
  const { roleIds } = useAuth();
  const [tasks, setTasks] = useState<RecordRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const match = (t: RecordRow) =>
        t.role === "ALL" || roleIds.includes(String(t.role) as (typeof roleIds)[number]);
      if (!config.apiUrl) {
        const all = LOCAL_SEED.tasks ?? [];
        setTasks(all.filter(match));
        return;
      }
      try {
        const data = await apiFetch<{ rows: RecordRow[] }>("/tasks", { roleId: roleIds.join("|") });
        if (!cancelled) setTasks(data.rows);
      } catch {
        const all = LOCAL_SEED.tasks ?? [];
        if (!cancelled) setTasks(all.filter(match));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [roleIds]);

  return tasks;
}
